import type {AfriexAdminTransaction} from '@/types';
import {
  AfriexTransactionStatus,
  AfriexTransactionType,
} from '@/types/Transaction';

import {getNigeriaBankName} from './bankUtil';
import {
  capitalizeWords,
  cleanText,
  compareAndFilterFields,
  formatTxChannel,
  formatValues,
  generateTxAmountFields,
  getTxTypeCheck,
  showCurrencyRate,
} from './dashboard';
import * as formatDate from './date';
import {getProcessorOwnId} from './processorOwnId';

interface DisplayField {
  [key: string]: string | string[] | null | undefined;
}

export function getFieldsToDisplay(
  transaction: AfriexAdminTransaction,
): DisplayField[] {
  const txType = getTxTypeCheck(transaction);
  const feeMeta = transaction?.meta?.fee;
  const deviceInfoFields = [
    {'Customer Device ID': transaction?.deviceInfo?.deviceId},
    {'Customer Device Type': transaction?.deviceInfo?.deviceType},
    {
      'Customer Device Name': transaction?.deviceInfo?.deviceName?.replace(
        /[{}]/g,
        '',
      ),
    },
  ];

  const feeFields = [
    {
      Fee: transaction.fee
        ? formatValues(transaction.fee, 'money', 4) +
          ` ${transaction.sourceAsset}`
        : null,
    },
    {'Fee Rule': JSON.stringify(feeMeta)},
  ];
  const promoTierFields = [
    {
      'Promo Tier': transaction?.meta?.promoApplied?.code
        ? capitalizeWords(
            transaction?.meta?.promoApplied?.code?.replace(/_/g, ' ') || '',
          )
        : null,
    },
    {
      'Tier Discount': transaction?.meta?.promoApplied?.discount
        ? formatValues(
            (transaction?.meta?.promoApplied?.discount ?? 0) * 100,
            'rate',
            6,
          ) + '%'
        : null,
    },
  ];

  const bankAccountName = {
    'Bank Account Name': transaction.destinationAccountDetails?.accountName,
  };
  const recipientName = {
    'Recipient Name': transaction.destinationAccountDetails?.accountName,
  };
  const accountNameFields = compareAndFilterFields(
    bankAccountName,
    recipientName,
  );

  const txId = {'Transaction ID': transaction.id};
  const ledgerTxId = {
    'Ledger Transaction IDs': transaction.ledgerTransactionIds,
  };
  const txIdFields = compareAndFilterFields(txId, ledgerTxId);

  const processorReference = {
    'Processor Reference': transaction.processorTransactionId,
  };
  const internalReference = {
    'Internal Reference': transaction.internalTransactionId,
  };
  const referenceFields = [
    ...compareAndFilterFields(processorReference, internalReference),
    {
      'Processor ID': getProcessorOwnId(
        transaction.processor,
        transaction.processorResponseMeta,
      ),
    },
  ];
  const processorFields = [
    {Processor: transaction.processor},
    {'Last-Mile Processor': transaction.lastMileProcessor},
  ];
  const txAmountFields = generateTxAmountFields(transaction);

  const reviewReasonField = {
    'Review Reason': transaction?.meta?.reviewReason || null,
  };

  // If status is PENDING/PROCESSING show statusMessage
  const withdrawMessage =
    transaction.status === AfriexTransactionStatus.PENDING
      ? transaction?.processorResponseMeta?.statusMessage
      : transaction?.processorResponseMeta?.statusDescription;

  const failureReason = transaction?.processorResponseMeta?.failureReason;

  switch (txType) {
    case AfriexTransactionType.WITHDRAW:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: withdrawMessage,
        },
        {
          'Failure Reason': failureReason
            ? `${failureReason.code}: ${failureReason.message}`
            : undefined,
        },
        ...txIdFields,
        {'Deposit Transaction ID': transaction?.depositTransactionReference},
        {'Transaction IP Address': transaction.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        ...processorFields,
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        {Channel: formatTxChannel(transaction)},
        ...feeFields,
        ...promoTierFields,
        {'Initiated By': transaction?.adminUser?.name},
        {
          Reason: capitalizeWords(
            (transaction.reason as string)?.replace(/_/g, ' '),
          ),
        },
        ...referenceFields,
        {
          'Session ID': transaction?.processorResponseMeta?.sessionId,
        },
        {
          'Bank name':
            transaction.destinationAccountDetails?.bankName ||
            getNigeriaBankName(transaction.destinationAccountDetails?.bankCode),
        },
        {
          'Bank account #':
            transaction.destinationAccountDetails?.accountNumber,
        },
        ...accountNameFields,
        {
          'Recipient Phone':
            transaction.destinationAccountDetails?.accountPhone,
        },
        {
          'Mobile Money Provider':
            transaction.destinationAccountDetails?.provider,
        },
        {'Tier ID': transaction?.tierId || 'None'},
        {
          'Customer ID':
            transaction?.sourceUserId || transaction?.destinationUserId,
        },
        {Status: transaction.status},
        reviewReasonField,
        ...deviceInfoFields,
      ];
    case AfriexTransactionType.DEPOSIT:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...feeFields,
        ...promoTierFields,
        {'Customer ID': transaction.sourceUserId},
        {Channel: formatTxChannel(transaction)},
        ...processorFields,

        {'Initiated By': transaction?.adminUser?.name},
        {
          Reason: capitalizeWords(
            (transaction?.reason as string)?.toString().replace(/_/g, ' '),
          ),
        },
        {
          'Stripe Payment Intent Id':
            transaction.processorResponseMeta?.paymentIntentId,
        },
        ...referenceFields,
        {Status: transaction.status},
        reviewReasonField,
        ...deviceInfoFields,
      ];
    case AfriexTransactionType.SWAP:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...feeFields,
        ...promoTierFields,
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        {'Customer ID': transaction.sourceUserId},
        {'Initiated By': transaction.initiatedBy},
        {Status: transaction.status},
        ...deviceInfoFields,
      ];
    case AfriexTransactionType.REVERSAL:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {Channel: formatTxChannel(transaction)},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...promoTierFields,
        ...referenceFields,
        {'Session ID': transaction?.processorResponseMeta?.sessionId},
        {'Bank name': transaction.destinationAccountDetails?.bankName},
        {
          'Bank account #':
            transaction.destinationAccountDetails?.accountNumber,
        },
        {
          'Bank account name':
            transaction.destinationAccountDetails?.accountName,
        },
        {
          'Recipient Name': transaction.destinationAccountDetails?.accountName,
        },
        {
          'Recipient Phone':
            transaction.destinationAccountDetails?.accountPhone,
        },
        {
          'Mobile Money Provider':
            transaction.destinationAccountDetails?.provider,
        },
        {'Customer ID': transaction.destinationUserId},
        {Status: transaction.status},
      ];
    case AfriexTransactionType.TRANSFER:
      return [
        {Type: `User to User`},
        {Description: transaction.processorResponseMeta?.statusDescription},
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {'Sender Username': transaction.sourceAccountDetails?.name},
        {'Receiver Username': transaction.destinationAccountDetails?.name},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        {Sender: transaction.sourceUserId},
        {Receiver: transaction.destinationUserId},
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...feeFields,
        ...promoTierFields,
        {Channel: formatTxChannel(transaction)},
        {Status: transaction.status},
        ...deviceInfoFields,
      ];
    case AfriexTransactionType.REFERRAL:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        {'Customer ID': transaction.sourceUserId},
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...promoTierFields,
        {Status: transaction.status},
        ...deviceInfoFields,
      ];
    case AfriexTransactionType.VIRTUAL_CARD_SPEND:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        ...processorFields,
        ...txAmountFields,
        {
          Merchant: cleanText(
            transaction?.processorResponseMeta?.merchant || 'N/A',
          ),
        },
        {'Currency Rate': showCurrencyRate(transaction)},
        ...feeFields,
        ...promoTierFields,
        {Channel: formatTxChannel(transaction)},
        {'Initiated By': transaction?.adminUser?.name},
        {
          Reason: capitalizeWords(
            (transaction?.reason as string)?.toString().replace(/_/g, ' '),
          ),
        },
        ...referenceFields,
        {'Customer ID': transaction.sourceUserId},
        {Status: transaction.status},
        ...deviceInfoFields,
      ];
    default:
      return [
        {
          Type: capitalizeWords(transaction.type?.replace(/_/g, ' ')),
        },
        {
          Description: transaction?.processorResponseMeta?.statusDescription,
        },
        ...txIdFields,
        {'Transaction IP Address': transaction.ip},
        {
          'Date Initiated': formatDate.dateTimeInTimeZone(
            transaction.createdAt?.toString(),
          ),
        },
        {
          'Date Updated': formatDate.dateTimeInTimeZone(
            transaction.updatedAt?.toString(),
          ),
        },
        ...processorFields,
        ...txAmountFields,
        {'Currency Rate': showCurrencyRate(transaction)},
        ...feeFields,
        ...promoTierFields,
        {Channel: formatTxChannel(transaction)},
        {'Initiated By': transaction?.adminUser?.name},
        {
          Reason: capitalizeWords(
            (transaction?.reason as string)?.toString().replace(/_/g, ' '),
          ),
        },
        ...referenceFields,
        {'Customer ID': transaction.sourceUserId},
        {Status: transaction.status},
        ...deviceInfoFields,
      ];
  }
}
